import { AlertTriangle, Inbox, Loader2, type LucideIcon } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

/** Loading placeholder shaped like the content it replaces, so nothing jumps on arrival. */
export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("relative overflow-hidden rounded bg-gray-4 animate-shimmer", className)}
      aria-hidden
      {...props}
    />
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-border" aria-busy>
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex h-10 items-center gap-3 px-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton
              key={c}
              className="h-3"
              // Uneven widths read as data; equal bars read as a progress bar.
              style={{ width: `${[28, 18, 14, 12, 10, 8][c % 6]}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("size-4 animate-spin text-text-subtle", className)} aria-hidden />;
}

/**
 * The empty state. Always says what the thing is and offers the action that creates one —
 * an empty table with no explanation reads as a bug.
 */
export function EmptyState({
  icon: Icon = Inbox, title, description, action, className,
}: {
  icon?: LucideIcon;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center px-6 py-14 text-center", className)}>
      <div className="mb-3 rounded-full border border-border bg-gray-2 p-2.5 dark:bg-gray-3">
        <Icon className="size-5 text-text-subtle" aria-hidden />
      </div>
      <p className="text-sm font-medium text-text">{title}</p>
      {description && <p className="mt-1 max-w-sm text-xs text-text-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Inline error. Shows the server's message rather than a generic apology — a 409 saying which
 *  user already holds an endpoint is far more useful than "something went wrong". */
export function ErrorState({
  error, title = "Something went wrong", action, className,
}: {
  error?: unknown;
  title?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  const message = error instanceof Error ? error.message : undefined;
  return (
    <div className={cn("flex flex-col items-center justify-center px-6 py-14 text-center", className)}>
      <div className="mb-3 rounded-full border border-danger-4 bg-danger-2 p-2.5">
        <AlertTriangle className="size-5 text-danger-11" aria-hidden />
      </div>
      <p className="text-sm font-medium text-text">{title}</p>
      {message && <p className="mt-1 max-w-md text-xs text-text-muted">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** A form-level error banner, for the failure that is about the submission as a whole. */
export function Callout({
  tone = "danger", children, className,
}: {
  tone?: "danger" | "warning" | "info";
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === "danger" ? "alert" : undefined}
      className={cn(
        "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
        tone === "danger" && "border-danger-4 bg-danger-2 text-danger-11",
        tone === "warning" && "border-warning-4 bg-warning-2 text-warning-11",
        tone === "info" && "border-accent-4 bg-accent-2 text-accent-11",
        className,
      )}
    >
      <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
      <div className="min-w-0">{children}</div>
    </div>
  );
}
